import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { Box, Grid, Typography } from '@mui/material';
import { toast } from 'react-toastify';

import { FHAntdDateTimePicker, FHInputField, FHSelect } from '../../../../base';
import COReadOnlyTextField from './COReadOnlyTextFields';
import { claimsActions } from '../../../../../store';
import { getUtcOffset } from '../../../../../utils/common';
import {
  disabledFutureDate,
  disabledFutureHoursAndMinutes,
  usDollarFormatter,
} from '../../../../../helpers';
import { ClaimExpectingPayment, ClaimSubmissionMethod } from '../enum';
import { ClaimStatus } from '../constants';
import {
  FORTA_BLUE,
  FORTA_GRAY4,
  FORTA_GRAY2,
  FORTA_GRAY3,
  FORTA_WHITE,
} from '../../../../../utils/colors';
import { buildClaimUpdatePaylod } from '../../claims/utils';

import type { RootState } from '../../../../../store';
import type { Moment } from 'moment';
import type { IClaim, SelectValueType } from '../../../../../utils/types';

interface IClaimOverviewInfoProps {
  isEditingInfo: boolean;
  allErrors: { [key: string]: boolean };
  onErrorUpdate: (err: { [key: string]: boolean }) => void;
}

interface IClaimOverviewInfoState {
  submission_method: string | null;
  is_payment_expected: string | null;
  date_submitted_to_clearinghouse: string | null;
  utc_offset_date_submitted_to_clearinghouse: number | null;
  clearing_house_claim_id: string | null;
  payor_claim_id: string | null;
  payor_appeal_tracking_id: string | null;
}

const claimOverviewInfoInitialState = {
  submission_method: null,
  is_payment_expected: null,
  date_submitted_to_clearinghouse: null,
  utc_offset_date_submitted_to_clearinghouse: null,
  clearing_house_claim_id: null,
  payor_claim_id: null,
  payor_appeal_tracking_id: null,
};

function ClaimOverviewInfo({
  isEditingInfo,
  onErrorUpdate,
  allErrors = {},
}: IClaimOverviewInfoProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: claim, loading: claimsLoading } = useSelector(
    (state: RootState) => state.claims,
  );
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});

  const [coInfoState, setCOInfoState] = useState<IClaimOverviewInfoState>(
    claimOverviewInfoInitialState,
  );

  const disableClaimInfoUpdate = claimsLoading || claim?.status === ClaimStatus.UNBILLABLE;

  const updateStateFromClaimData = (claim: IClaim) => {
    setCOInfoState({
      submission_method: claim.submission_method ?? null,
      is_payment_expected: claim.is_payment_expected ?? null,
      date_submitted_to_clearinghouse: claim.date_submitted_to_clearinghouse ?? null,
      utc_offset_date_submitted_to_clearinghouse:
        claim.utc_offset_date_submitted_to_clearinghouse ?? null,
      /* As backend has validation for clearing_house_claim_id and payor_claim_id to be
      at least 1 character long, we are setting it to null if it is an empty string */
      clearing_house_claim_id:
        claim.clearing_house_claim_id === '' ? null : (claim.clearing_house_claim_id ?? null),
      payor_claim_id: claim.payor_claim_id === '' ? null : (claim.payor_claim_id ?? null),
      payor_appeal_tracking_id:
        claim.payor_appeal_tracking_id === '' ? null : (claim.payor_appeal_tracking_id ?? null),
    });
  };

  useEffect(() => {
    if (!claim) return;
    updateStateFromClaimData(claim);
    if (
      claim?.status !== ClaimStatus.GENERATED &&
      claim?.status !== ClaimStatus.UNBILLABLE &&
      !claim.date_submitted_to_clearinghouse
    ) {
      setErrors({ date_submitted_to_clearinghouse: true });
      onErrorUpdate({ date_submitted_to_clearinghouse: true });
    } else {
      setErrors({});
      onErrorUpdate({ date_submitted_to_clearinghouse: false });
    }
  }, [claim]);

  const handleDateTimePickerChange = (
    dateKey: keyof IClaimOverviewInfoState,
    newValue: Moment,
    save?: boolean,
  ) => {
    let val = newValue;
    if (moment(newValue, 'minute').isAfter(moment(new Date()), 'minute')) {
      val = moment(newValue).hours(0).minutes(0).seconds(0).milliseconds(0);
    }

    if (
      dateKey === 'date_submitted_to_clearinghouse' &&
      newValue &&
      moment(val).toISOString() !== moment(claim?.date_submitted_to_clearinghouse).toISOString()
    ) {
      if (
        claim?.date_finalized &&
        moment(moment(val).format('YYYY-MM-DD')).isAfter(claim.date_finalized)
      ) {
        toast.error('Date submitted should be less than date finalized');
        return;
      }
      const err = { ...errors, date_submitted_to_clearinghouse: false };
      setErrors(err);
      onErrorUpdate(err);
    }

    if (save) {
      if (
        moment(val).toISOString() === moment(claim?.date_submitted_to_clearinghouse).toISOString()
      ) {
        return;
      }
      handleSaveField({
        [dateKey]: moment(val).toISOString(),
        utc_offset_date_submitted_to_clearinghouse:
          dateKey === 'date_submitted_to_clearinghouse'
            ? getUtcOffset(moment(new Date()))
            : coInfoState.utc_offset_date_submitted_to_clearinghouse,
      });
    } else {
      setCOInfoState({
        ...coInfoState,
        [dateKey]: moment(val).toISOString(),
        utc_offset_date_submitted_to_clearinghouse:
          dateKey === 'date_submitted_to_clearinghouse'
            ? getUtcOffset(moment(new Date()))
            : coInfoState.utc_offset_date_submitted_to_clearinghouse,
      });
    }
  };

  const handleSaveField = async (updates: any, field?: keyof IClaim) => {
    if (!claim) return;
    for (const key in allErrors) {
      if (allErrors[key] && key !== field && !('date_submitted_to_clearinghouse' in updates)) {
        toast.error('Ensure all highlighted fields are completed.');
        updateStateFromClaimData(claim);
        if (
          claim?.status !== ClaimStatus.GENERATED &&
          claim?.status !== ClaimStatus.UNBILLABLE &&
          !claim.date_submitted_to_clearinghouse
        ) {
          setErrors({ date_submitted_to_clearinghouse: true });
          onErrorUpdate({ date_submitted_to_clearinghouse: true });
        } else {
          setErrors({});
          onErrorUpdate({ date_submitted_to_clearinghouse: false });
        }
        return;
      }
    }
    if (field && claim[field] === updates[field]) {
      return;
    }

    setCOInfoState({
      ...coInfoState,
      ...updates,
    });

    const data = buildClaimUpdatePaylod(claim, updates);
    const response = await dispatch(
      claimsActions.updateClaim({
        getAccessTokenSilently,
        claimId: claim?.id,
        data,
      }),
    );

    if (!response.payload) {
      setCOInfoState({
        submission_method: claim.submission_method ?? null,
        is_payment_expected: claim.is_payment_expected ?? null,
        date_submitted_to_clearinghouse: claim.date_submitted_to_clearinghouse ?? null,
        utc_offset_date_submitted_to_clearinghouse:
          claim.utc_offset_date_submitted_to_clearinghouse ?? null,
        clearing_house_claim_id:
          claim.clearing_house_claim_id === '' ? null : (claim.clearing_house_claim_id ?? null),
        payor_claim_id: claim.payor_claim_id === '' ? null : (claim.payor_claim_id ?? null),
        payor_appeal_tracking_id:
          claim.payor_appeal_tracking_id === '' ? null : (claim.payor_appeal_tracking_id ?? null),
      });
    }
  };

  return (
    <Box data-testid="claimOverviewInfoContainer">
      <Typography textAlign="left" fontSize={21} color={FORTA_BLUE.primary} mb={2}>
        Submission info
      </Typography>
      <Grid container spacing={2} alignItems={'center'}>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Expecting Payment
          </Typography>
          {isEditingInfo ? (
            <FHSelect
              data-testid="coExpectingPaymentDropdown"
              fullWidth
              displayEmpty
              disabled={disableClaimInfoUpdate}
              inputHeight={40}
              sx={{
                boxShadow: 'none',
                borderRadius: '8px',
                '& input::placeholder': { fontSize: '14px', color: FORTA_GRAY4 },
                '&.Mui-disabled': {
                  WebkitTextFillColor: FORTA_GRAY3,
                  cursor: 'not-allowed',
                },
              }}
              options={Object.values(ClaimExpectingPayment)
                .map((item) => ({
                  label: item,
                  value: item,
                }))
                .sort((a, b) => {
                  const aLabel = a.label.toLowerCase();
                  const bLabel = b.label.toLowerCase();
                  if (aLabel < bLabel) return -1;
                  if (aLabel > bLabel) return 1;
                  return 0;
                })}
              renderValue={
                coInfoState.is_payment_expected === null
                  ? () => (
                      <Typography color={FORTA_GRAY3} fontSize={14}>
                        Select
                      </Typography>
                    )
                  : undefined
              }
              value={coInfoState.is_payment_expected ?? ''}
              onUpdate={(value: SelectValueType) => {
                handleSaveField({
                  is_payment_expected: value,
                });
              }}
            />
          ) : (
            <COReadOnlyTextField
              value={
                coInfoState.is_payment_expected === null ? null : coInfoState.is_payment_expected
              }
            />
          )}
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Submission Method
          </Typography>
          {isEditingInfo ? (
            <FHSelect
              data-testid="coSubmissionMethodDropdown"
              fullWidth
              displayEmpty
              inputHeight={40}
              disabled={disableClaimInfoUpdate}
              sx={{
                boxShadow: 'none',
                borderRadius: '8px',
                '& input::placeholder': { fontSize: '14px', color: FORTA_GRAY4 },
                '&.Mui-disabled': {
                  WebkitTextFillColor: FORTA_GRAY3,
                  cursor: 'not-allowed',
                },
              }}
              options={Object.values(ClaimSubmissionMethod)
                .map((item) => ({
                  value: item,
                  label: item,
                }))
                .sort((a, b) => {
                  const aLabel = a.label.toLowerCase();
                  const bLabel = b.label.toLowerCase();
                  if (aLabel < bLabel) return -1;
                  if (aLabel > bLabel) return 1;
                  return 0;
                })}
              optionDataTestIdPrefix=""
              renderValue={
                coInfoState.submission_method === null
                  ? () => (
                      <Typography color={FORTA_GRAY3} fontSize={14}>
                        Select
                      </Typography>
                    )
                  : undefined
              }
              value={coInfoState.submission_method ?? ''}
              onUpdate={(value: SelectValueType) => {
                handleSaveField({
                  submission_method: String(value),
                });
              }}
            />
          ) : (
            <COReadOnlyTextField
              value={coInfoState.submission_method === null ? null : coInfoState.submission_method}
            />
          )}
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Date submitted
          </Typography>
          {isEditingInfo ? (
            <FHAntdDateTimePicker
              data-testid="coDateSubmitted"
              inputHeight={40}
              sx={{
                width: '100%',
                boxShadow: 'none',
                borderRadius: '8px',
                '&.ant-picker-disabled': {
                  backgroundColor: FORTA_WHITE,
                  '& .ant-picker-input>input[disabled]': {
                    color: FORTA_GRAY3,
                  },
                },
              }}
              format="MM/DD/YYYY h:mm a"
              showTime={{ format: 'h:mm a' }}
              allowClear={false}
              showNow={true}
              use12Hours
              // Unbillable claims have no submit date: disable the field and show it
              // blank. disableClaimInfoUpdate already encodes UNBILLABLE.
              disabled={disableClaimInfoUpdate}
              disabledIconColor={FORTA_GRAY2}
              disabledDate={disabledFutureDate}
              disabledTime={(current: Moment) => disabledFutureHoursAndMinutes(current)}
              error={errors['date_submitted_to_clearinghouse']}
              value={
                claim?.status !== ClaimStatus.UNBILLABLE &&
                coInfoState.date_submitted_to_clearinghouse
                  ? moment(coInfoState?.date_submitted_to_clearinghouse)
                  : ''
              }
              onChange={(value: Moment) => {
                handleDateTimePickerChange(
                  'date_submitted_to_clearinghouse',
                  value?.seconds(0).milliseconds(0),
                );
              }}
              onSelect={(value: Moment) => {
                handleDateTimePickerChange(
                  'date_submitted_to_clearinghouse',
                  value?.seconds(0).milliseconds(0),
                );
              }}
              onBlur={() => {
                handleDateTimePickerChange(
                  'date_submitted_to_clearinghouse',
                  moment(coInfoState.date_submitted_to_clearinghouse),
                  true,
                );
              }}
              onOk={(value: Moment) => {
                handleDateTimePickerChange(
                  'date_submitted_to_clearinghouse',
                  value?.seconds(0).milliseconds(0),
                  true,
                );
              }}
            />
          ) : (
            <COReadOnlyTextField
              value={
                claim?.status !== ClaimStatus.UNBILLABLE &&
                coInfoState?.date_submitted_to_clearinghouse
                  ? moment(coInfoState?.date_submitted_to_clearinghouse).format(
                      'MM/DD/YYYY - hh:mm a',
                    )
                  : null
              }
            />
          )}
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Clearinghouse claim ID
          </Typography>
          {isEditingInfo ? (
            <FHInputField
              fullWidth
              inputHeight={40}
              inputProps={{
                'data-testid': 'coClearingHouseClaimId',
              }}
              disabled={disableClaimInfoUpdate}
              placeholder="Enter ID"
              sx={{
                boxShadow: 'none',
                borderRadius: '8px',
                '& input::placeholder': { fontSize: '14px', color: FORTA_GRAY4 },
                '&.Mui-disabled input': {
                  WebkitTextFillColor: `${FORTA_GRAY3} !important`,
                  cursor: 'not-allowed',
                },
              }}
              value={coInfoState.clearing_house_claim_id ?? ''}
              onChange={(e) => {
                setCOInfoState({
                  ...coInfoState,
                  clearing_house_claim_id: e.target.value ? e.target.value : null,
                });
              }}
              onBlur={() =>
                handleSaveField(
                  { clearing_house_claim_id: coInfoState.clearing_house_claim_id },
                  'clearing_house_claim_id',
                )
              }
            />
          ) : (
            <COReadOnlyTextField value={coInfoState.clearing_house_claim_id ?? null} />
          )}
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems={'center'} mt={2}>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Submitter
          </Typography>
          <COReadOnlyTextField
            value={claim ? `${claim?.biller.first_name} ${claim?.biller.last_name}` : null}
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Total amount submitted
          </Typography>
          <COReadOnlyTextField
            value={
              claim?.form_1500_data?.total_charge
                ? usDollarFormatter.format(claim?.form_1500_data?.total_charge || 0)
                : null
            }
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Code billed (Procedure code)
          </Typography>
          <COReadOnlyTextField
            value={
              claim?.service_code
                ? `${claim?.service_code.label} (${claim?.service_code.code})`
                : null
            }
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Payor
          </Typography>
          <COReadOnlyTextField value={claim?.form_1500_data?.payor_id ?? null} />
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems={'center'} mt={2}>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            State billed
          </Typography>
          <COReadOnlyTextField
            value={
              claim?.form_1500_data?.service_facility_address?.street_address_state?.toUpperCase() ??
              null
            }
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Payor claim ID
          </Typography>
          {isEditingInfo ? (
            <FHInputField
              fullWidth
              inputHeight={40}
              inputProps={{
                'data-testid': 'coPayorClaimId',
              }}
              disabled={disableClaimInfoUpdate}
              placeholder="Enter ID"
              sx={{
                boxShadow: 'none',
                borderRadius: '8px',
                '& input::placeholder': { fontSize: '14px', color: FORTA_GRAY4 },
                '&.Mui-disabled input': {
                  WebkitTextFillColor: `${FORTA_GRAY3} !important`,
                  cursor: 'not-allowed',
                },
              }}
              value={coInfoState.payor_claim_id ?? ''}
              onChange={(e) => {
                setCOInfoState({
                  ...coInfoState,
                  payor_claim_id: e.target.value,
                });
              }}
              onBlur={() =>
                handleSaveField({ payor_claim_id: coInfoState.payor_claim_id }, 'payor_claim_id')
              }
            />
          ) : (
            <COReadOnlyTextField value={coInfoState.payor_claim_id ?? null} />
          )}
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <Typography fontWeight="bold" textAlign="left" mb={1} data-testid="payorAppealTrackingId">
            Payor appeal tracking ID
          </Typography>
          {isEditingInfo ? (
            <FHInputField
              fullWidth
              inputHeight={40}
              inputProps={{
                'data-testid': 'coPayorAppealTrackingId',
              }}
              disabled={disableClaimInfoUpdate}
              placeholder="Enter ID"
              sx={{
                boxShadow: 'none',
                borderRadius: '8px',
                '& input::placeholder': { fontSize: '14px', color: FORTA_GRAY4 },
                '&.Mui-disabled input': {
                  WebkitTextFillColor: `${FORTA_GRAY3} !important`,
                  cursor: 'not-allowed',
                },
              }}
              value={coInfoState.payor_appeal_tracking_id ?? ''}
              onChange={(e) => {
                setCOInfoState({
                  ...coInfoState,
                  payor_appeal_tracking_id: e.target.value,
                });
              }}
              onBlur={() =>
                handleSaveField(
                  { payor_appeal_tracking_id: coInfoState.payor_appeal_tracking_id },
                  'payor_appeal_tracking_id',
                )
              }
            />
          ) : (
            <COReadOnlyTextField value={coInfoState.payor_appeal_tracking_id ?? null} />
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default ClaimOverviewInfo;
